import { captureMessage } from "@/shared/services/sentry.service";

/**
 * Canal de communication entre la fenêtre de consentement Bridge (qui atterrit
 * sur BridgeCallbackPage au retour) et les onglets Console d'origine. Sans lui,
 * l'onglet qui a initié le virement n'apprend jamais son issue (ADM-864) : Bridge
 * ne fait que rediriger la fenêtre, sans statut dans l'URL de retour.
 */
const BRIDGE_TRANSFER_CHANNEL_BASE = "yeati:bridge-transfer";

// Node relaie les BroadcastChannel entre workers Vitest. Un canal propre à
// chaque graphe de test évite que deux scénarios exécutés en parallèle pilotent
// mutuellement leurs listeners ; le nom de production reste stable entre tabs.
export const BRIDGE_TRANSFER_CHANNEL = import.meta.env.MODE === "test"
  ? `${BRIDGE_TRANSFER_CHANNEL_BASE}:${crypto.randomUUID()}`
  : BRIDGE_TRANSFER_CHANNEL_BASE;

/**
 * Le mouvement n'existe que pour ce virement (chemin « solder par règlement ») :
 * il doit disparaître avec l'initiation. Faux sur la page des mouvements, où le
 * mouvement préexiste et n'est jamais supprimé.
 */
type DiscardFlag = { discardMovement?: boolean };

/**
 * Union discriminée : chaque consommateur est obligé de traiter les quatre cas,
 * ce qui évite qu'un chemin monétaire soit oublié.
 *
 * `returned` — la fenêtre de consentement est revenue sur l'URL de callback.
 * Cette URL ne porte plus le statut, donc le retour
 * ne prouve rien : il dit seulement que l'utilisateur n'a pas abandonné en
 * chemin. L'issue est résolue ensuite auprès de l'API, par un onglet vivant.
 * `confirmed` — issue connue. `success` = consentement donné (et date d'émission
 * enregistrée ou non) ; `failure` = jamais consenti ou refusé, l'initiation a
 * été libérée et rien ne doit être soldé.
 * `abandoned` — fenêtre fermée sans jamais revenir. Le hold sur le mouvement est
 * libéré par le listener applicatif, l'API vérifiant auprès de Bridge.
 * `released` — initiation déjà libérée par l'émetteur, qui a informé
 * l'utilisateur : les autres onglets n'ont plus qu'à se rafraîchir.
 */
export type BridgeTransferResultMessage =
  | ({ movementId: string, phase: "returned" } & DiscardFlag)
  | {
    movementId: string
    phase: "confirmed"
    status: "success" | "failure"
    /** `success` : false = validé chez Bridge mais date d'émission non enregistrée. */
    issueDateRecorded?: boolean
  }
  | ({ movementId: string, phase: "abandoned" } & DiscardFlag)
  | { movementId: string, phase: "released" };

export type BridgeTransferPhase = BridgeTransferResultMessage["phase"];

const isBroadcastSupported = () => typeof BroadcastChannel !== "undefined";

const RESULT_STORAGE_PREFIX = `${BRIDGE_TRANSFER_CHANNEL}:result:`;
const RESULT_TTL_MS = 30 * 60 * 1000;

type StoredBridgeTransferResult = {
  message: BridgeTransferResultMessage
  storedAt: number
};

const storageKey = (movementId: string) => `${RESULT_STORAGE_PREFIX}${movementId}`;

const isBridgeTransferResult = (value: unknown): value is BridgeTransferResultMessage => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<BridgeTransferResultMessage>;
  return typeof candidate.movementId === "string"
    && ["returned", "confirmed", "abandoned", "released"].includes(candidate.phase ?? "");
};

const persistBridgeTransferResult = (message: BridgeTransferResultMessage): void => {
  try {
    const stored: StoredBridgeTransferResult = { message, storedAt: Date.now() };
    window.localStorage.setItem(storageKey(message.movementId), JSON.stringify(stored));
  }
  catch {
    // Le stockage peut être refusé (navigation privée/politique navigateur).
    // BroadcastChannel reste alors le mécanisme de repli temps réel.
  }
};

export const clearBridgeTransferResult = (movementId: string): void => {
  try {
    window.localStorage.removeItem(storageKey(movementId));
  }
  catch {
    // Même repli que ci-dessus : l'absence de stockage ne bloque pas le flux.
  }
};

const readStoredBridgeTransferResults = (): BridgeTransferResultMessage[] => {
  try {
    const now = Date.now();
    const messages: BridgeTransferResultMessage[] = [];
    for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
      const key = window.localStorage.key(index);
      if (!key?.startsWith(RESULT_STORAGE_PREFIX)) continue;

      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const stored = JSON.parse(raw) as Partial<StoredBridgeTransferResult>;
      if (
        typeof stored.storedAt !== "number"
        || now - stored.storedAt > RESULT_TTL_MS
        || !isBridgeTransferResult(stored.message)
      ) {
        window.localStorage.removeItem(key);
        continue;
      }
      messages.push(stored.message);
    }
    return messages;
  }
  catch {
    return [];
  }
};

/** Émis par BridgeCallbackPage vers les autres onglets/fenêtres Console. */
export const broadcastBridgeTransferResult = (message: BridgeTransferResultMessage): void => {
  // Persister avant de diffuser ferme la course où la popup termine puis se
  // ferme avant que l'onglet d'origine ait installé son abonnement.
  persistBridgeTransferResult(message);
  if (!isBroadcastSupported()) return;
  const channel = new BroadcastChannel(BRIDGE_TRANSFER_CHANNEL);
  channel.postMessage(message);
  channel.close();
};

/** S'abonne aux résultats de virement Bridge. Renvoie la fonction de désabonnement. */
export const subscribeToBridgeTransferResults = (
  handler: (message: BridgeTransferResultMessage) => void,
): (() => void) => {
  let active = true;
  const lastFingerprints = new Map<string, string>();
  const deliver = (message: BridgeTransferResultMessage) => {
    if (!active || !isBridgeTransferResult(message)) return;
    const fingerprint = JSON.stringify(message);
    if (lastFingerprints.get(message.movementId) === fingerprint) return;
    lastFingerprints.set(message.movementId, fingerprint);
    handler(message);
  };

  const channel = isBroadcastSupported()
    ? new BroadcastChannel(BRIDGE_TRANSFER_CHANNEL)
    : null;
  if (channel) {
    channel.onmessage = (event: MessageEvent<BridgeTransferResultMessage>) => deliver(event.data);
  }

  const onStorage = (event: StorageEvent) => {
    if (!event.key?.startsWith(RESULT_STORAGE_PREFIX) || !event.newValue) return;
    try {
      const stored = JSON.parse(event.newValue) as Partial<StoredBridgeTransferResult>;
      if (isBridgeTransferResult(stored.message)) deliver(stored.message);
    }
    catch {
      // Une valeur corrompue ou écrite par une ancienne version est ignorée.
    }
  };
  window.addEventListener("storage", onStorage);

  // Le replay asynchrone laisse au composant appelant le temps de conserver la
  // fonction de désabonnement avant que son handler puisse être invoqué.
  queueMicrotask(() => readStoredBridgeTransferResults().forEach(deliver));

  return () => {
    active = false;
    channel?.close();
    window.removeEventListener("storage", onStorage);
  };
};

const CONSENT_WINDOW_FEATURES = "width=600,height=700";
const POLL_INTERVAL_MS = 300;
const CALLBACK_PATH = "/accounting/bridge/callback";
/**
 * Petit délai entre la détection de fermeture et l'avertissement, pour laisser
 * arriver un message émis juste avant que la fenêtre ne disparaisse.
 */
const ABANDON_GRACE_MS = 2000;
/** Au-delà, on cesse de surveiller : un consentement Bridge ne dure pas 30 minutes. */
const MAX_WATCH_MS = 30 * 60 * 1000;

/** Peut renvoyer null si le navigateur bloque l'ouverture (popup blocker). */
export const openBridgeConsentWindow = (consentUrl: string): Window | null =>
  window.open(consentUrl, "_blank", CONSENT_WINDOW_FEATURES);

/**
 * L'URL de la fenêtre de consentement est illisible (cross-origin) tant qu'elle
 * est chez Bridge, et devient lisible dès la redirection vers notre domaine.
 * C'est le seul signal de retour qui ne dépende pas du JS de la popup : la route
 * de callback étant lazy-loadée, son chunk peut ne jamais finir de charger si
 * l'utilisateur ferme la fenêtre aussitôt redirigé (ADM-864).
 */
const hasReachedCallback = (consentWindow: Window): boolean => {
  try {
    return consentWindow.location.pathname.includes(CALLBACK_PATH);
  }
  catch {
    // Toujours côté Bridge : accès cross-origin refusé.
    return false;
  }
};

/**
 * Surveille la fenêtre de consentement : diffuse `returned` dès qu'elle atteint
 * l'URL de callback, et `abandoned` si elle est fermée sans jamais y arriver.
 * Vit au niveau module pour survivre au démontage du composant initiateur.
 *
 * C'est volontairement l'onglet vivant qui détecte le retour : l'issue
 * doit être demandée à l'API, et la popup meurt souvent avant d'avoir pu le
 * faire.
 *
 * `discardOnAbandon` : le mouvement a été créé pour ce virement et doit être
 * supprimé si l'initiation est libérée.
 */
export const watchBridgeConsentWindow = (
  consentWindow: Window,
  movementId: string,
  { discardOnAbandon = false }: { discardOnAbandon?: boolean } = {},
): void => {
  // Sans canal, impossible de distinguer un abandon d'un succès : on ne surveille pas.
  if (!isBroadcastSupported()) return;

  let resolved = false;
  let intervalId = 0;
  const startedAt = Date.now();

  // N'importe quelle phase suffit : le retour ou l'issue rendent la surveillance inutile.
  const unsubscribe = subscribeToBridgeTransferResults((message) => {
    if (message.movementId !== movementId) return;
    resolved = true;
    unsubscribe();
    window.clearInterval(intervalId);
  });

  intervalId = window.setInterval(() => {
    if (hasReachedCallback(consentWindow)) {
      resolved = true;
      window.clearInterval(intervalId);
      unsubscribe();
      // La popup émettra peut-être le même message : le listener déduplique.
      broadcastBridgeTransferResult({ movementId, phase: "returned", discardMovement: discardOnAbandon });
      return;
    }

    // Fenêtre laissée ouverte indéfiniment : on arrête de sonder plutôt que de
    // garder un timer à vie dans l'onglet. Le mouvement reste récupérable via
    // « Reprendre le virement ».
    if (Date.now() - startedAt > MAX_WATCH_MS) {
      window.clearInterval(intervalId);
      unsubscribe();
      return;
    }

    if (!consentWindow.closed) return;
    window.clearInterval(intervalId);
    window.setTimeout(() => {
      unsubscribe();
      if (resolved) return;
      captureMessage(
        `[BridgeTransfer] Fenêtre de consentement fermée sans résultat (mouvement ${movementId})`,
        "warning",
      );
      // La libération est portée par le listener applicatif : shared ne peut pas
      // appeler le service accounting, et l'onglet reste vivant pour le faire.
      broadcastBridgeTransferResult({ movementId, phase: "abandoned", discardMovement: discardOnAbandon });
    }, ABANDON_GRACE_MS);
  }, POLL_INTERVAL_MS);
};
